import {
  IOrder,
  Note,
  Message,
  Prescription,
  PrescriptionDTO,
  UpdatePrescriptionDTO,
  MessageDTO,
  EMR_API,
  IAdminTokenPayload,
  NewConsultationDTO,
  IOrderLineItem,
  IProductItem,
  ICategoryItem,
  IOrderDTO,
  Drug,
  DrugDTO,
  UpdateCustomerDTO,
  UpdateConsultationDTO,
  IAdminNotification,
  IAppointment,
  ApplyInvoiceDiscountDTO,
  ICustomerUser,
  ICancelSubscriptionQuery,
  ILabReq,
  ILabReqDTO,
  IChatAuth,
  JoinChatResponse,
  UpdateAppointmentDTO,
  IFile,
  CRMUserFilters,
  GetAppointmentsQueryDto,
  CreateAppointmentDto,
  IAppointmentVirtualMeeting,
  NewCustomerDTO,
  ILead,
  IOrderRequirement,
  IConsultation,
  IAdminUser,
  IActivity,
  IBaseListResponse,
  CreateActivityDTO,
  IProductV2,
  PharmacyConfig,
  ICRMUser,
  LabResult,
  ILabResultDTO,
  LabResultPatientData,
  IOrderRefundData,
  IGetOrdersDTO,
  IOrderPayment,
  PrescriptionsQuery,
  UploadFileResponse,
  IPharmacy,
  IPharmacyListQuery,
  ICreatePharmacyRequest,
  IUpdatePharmacyRequest
} from '@care-portals/api-interfaces';
import { convertToHttpParams } from '@care-portals/shared';
import axios from 'axios';
import type { FilterQuery } from 'mongoose';

interface RequestParams {
  query?: { [param: string]: any };
  body?: Object;
}

export class EMR {
  BASE_URL: string;
  AUTH_URL: string;
  user: IAdminTokenPayload;
  http = axios;
  token: string;
  organization: string;
  username: string;
  password: string;

  constructor(
    org: string,
    config?: {
      baseUrl?: string;
      authUrl?: string;
      username?: string;
      password?: string;
      token?: string;
    }
  ) {
    this.organization = org;
    let env: any = {};
    if (typeof process !== 'undefined') {
      env = process.env;
    }
    this.BASE_URL = config?.baseUrl || env?.EMR_API;
    this.AUTH_URL = config?.authUrl || env?.AUTH_API;
    this.username = config?.username || env?.AUTH_USERNAME;
    this.password = config?.password || env?.AUTH_PASSWORD;
    this.token = config?.token;
  }
  private async _requestObs(
    method: string,
    endpoint: string,
    params?: RequestParams
  ) {
    params = params || {};
    const url = this.BASE_URL + endpoint;

    if (!this.token) {
      await this.authenticate().catch(err => {
        throw new Error(err.message || 'Authentication Failed');
      });
    }
    try {
      let response: Promise<any>;
      if (method === 'get' || method === 'delete') {
        response = await this.http[method](url, {
          params: params.query,
          headers: {
            Authorization: 'Bearer ' + this.token,
            organization: this.organization
          }
        }).then(res => res.data);
      } else {
        // method = POST | PUT  etc..
        response = await this.http[method](url, params.body, {
          headers: {
            Authorization: 'Bearer ' + this.token,
            organization: this.organization
          }
        }).then(res => res.data);
      }

      return response;
    } catch (error: any) {
      error =
        error?.response?.data ||
        error?.response ||
        error?.code ||
        error.errors?.[0] ||
        error;
      console.error('error response>>', error);

      throw error?.message
        ? new Error(error?.message)
        : error || new Error('Unknown ERROR; check logs');
    }
  }

  async authenticate() {
    this.token = await this.http
      .post(this.AUTH_URL, {
        username: this.username,
        password: this.password
      })
      .then(res => {
        this.user = res.data
        return res?.data?.token;
      })
      .catch(err => {
        throw new Error('Authentication Failed');
      });
  }
  ///////////////////////
  ///  ADMIN NOTIFICATIONS
  //////////////////////
  getAdminNotifications(): Promise<IAdminNotification[]> {
    try {
      return this._requestObs('get', EMR_API.notifications.base);
    } catch (error) {
      console.error(error);
      throw new Error('Failed to get notifications: ' + error);
    }
  }

  dismissAllNotifications(): Promise<any> {
    try {
      return this._requestObs(
        'get',
        EMR_API.notifications.base + EMR_API.notifications.dismissAll,
        {}
      );
    } catch (error) {
      console.error(error);
      throw new Error('Failed to mark all notification as read: ' + error);
    }
  }

  dismissSingleNotification(notificationId: string): Promise<any> {
    try {
      return this._requestObs(
        'get',
        EMR_API.notifications.base +
        EMR_API.notifications.dismissSingle.replace(':id', notificationId),
        {}
      );
    } catch (error) {
      console.error(error);
      throw new Error(
        `Failed to mark notification ${notificationId} as read: ` + error
      );
    }
  }

  ////////////////////////
  // CUSTOMERS FUNCTIONS
  ////////////////////////
  async getCustomers(filters: any): Promise<ICustomerUser[]> {
    return await this._requestObs('get', EMR_API.customers.base, {
      query: filters
    });
  }

  async getCustomer(id: string): Promise<ICustomerUser> {
    return await this._requestObs(
      'get',
      EMR_API.customers.base + EMR_API.customers.single.replace(':id', id)
    );
  }

  async updateCustomer(
    customerId: string,
    customerDTO: UpdateCustomerDTO
  ): Promise<ICustomerUser> {
    return await this._requestObs(
      'put',
      EMR_API.customers.base +
      EMR_API.customers.single.replace(':id', customerId),
      {
        body: customerDTO
      }
    );
  }

  async createCustomer(
    customer: NewCustomerDTO,
    lead?: ILead
  ): Promise<ICustomerUser> {
    return await this._requestObs('post', EMR_API.customers.base, {
      body: { customer, lead }
    });
  }

  async getCustomerPaymentMethods(id: string): Promise<any> {
    return await this._requestObs(
      'get',
      EMR_API.customers.base +
      EMR_API.customers.paymentMethods.replace(':id', id)
    );
  }

  async updateWatchlist(customerId: string, isAdd: boolean) {
    return await this._requestObs(
      'put',

      EMR_API.customers.base +
      EMR_API.customers.watchlist.replace(':id', customerId),
      { body: isAdd }
    );
  }

  ////////////////
  /// ORDERS
  ///////////////
  getAllOrders(
    filter?: IGetOrdersDTO
  ): Promise<IOrder[]> | Promise<IBaseListResponse<IOrder>> {
    // remove nulls and undefined properties from the request
    filter = Object.fromEntries(
      Object.entries(filter).filter(([_, v]) => v != null)
    );

    return this._requestObs('get', EMR_API.orders.base, {
      query: { ...filter }
    });
  }
  async getOrder(orderId): Promise<IOrder> {
    return await this._requestObs(
      'get',
      EMR_API.orders.base + EMR_API.orders.single.replace(':orderId', orderId)
    );
  }

  createOrder(items: IOrderLineItem[], customerId: string): Promise<any> {
    return this._requestObs('post', EMR_API.orders.base, {
      body: { items, customerId }
    });
  }

  async updateOrder(
    orderId: number | string,
    data: IOrderDTO
  ): Promise<IOrder> {
    return await this._requestObs(
      'put',
      EMR_API.orders.base +
      EMR_API.orders.single.replace(':orderId', orderId.toString()),
      {
        body: { data }
      }
    );
  }

  getOrderRequirements(orderId: string): Promise<IOrderRequirement[]> {
    return this._requestObs(
      'get',
      EMR_API.orders.base +
      EMR_API.orders.singleRequirements.replace(':orderId', orderId)
    );
  }

  applyDiscountForInvoice(body: ApplyInvoiceDiscountDTO) {
    return this._requestObs(
      'post',
      EMR_API.orders.base + EMR_API.orders.singleDiscount,
      { body }
    );
  }

  orders = {
    list: async (filters?: IGetOrdersDTO): Promise<IOrder[]> =>
      await this._requestObs('get', EMR_API.orders.base, {
        query: {
          ...filters,
          // flatten extras and meta objects into query params
          ...(filters?.extras
            ? Object.fromEntries(
              Object.entries(filters.extras).map(([key, value]) => [
                `extras.${key}`,
                value
              ])
            )
            : {}),
          ...(filters?.meta
            ? Object.fromEntries(
              Object.entries(filters.meta).map(([key, value]) => [
                `meta.${key}`,
                value
              ])
            )
            : {})
        }
      }),
    update: async (
      orderId: string,
      data: Partial<IOrder> & { sendEvent?: boolean }
    ): Promise<IOrder> =>
      await this._requestObs('put', EMR_API.orders.base + EMR_API.orders.single.replace(':orderId', orderId), {
        body: { data }
      }),
    get: async (orderId: string): Promise<IOrder> =>
      await this._requestObs('get', EMR_API.orders.base + EMR_API.orders.single.replace(':orderId', orderId)),
    create: async (
      items: IOrderLineItem[],
      customerId: string
    ): Promise<IOrder> =>
      await this._requestObs('post', EMR_API.orders.base, {
        body: { items, customerId }
      }),
    delete: async (orderId: string): Promise<void> =>
      await this._requestObs(
        'delete',
        EMR_API.orders.base + EMR_API.orders.single.replace(':orderId', orderId)
      ),
    charge: async (orderId: string): Promise<IOrderPayment> =>
      await this._requestObs(
        'post',
        EMR_API.orders.base +
        EMR_API.orders.payments.replace(':orderId', orderId)
      ),
    refund: async (
      orderId: string,
      refundData: IOrderRefundData
    ): Promise<IOrderPayment> =>
      await this._requestObs(
        'post',
        EMR_API.orders.base +
        EMR_API.orders.refund.replace(':orderId', orderId),
        {
          body: refundData
        }
      )
  };

  //////////////////
  /// CONSULTATIONS
  /////////////////

  getConsultation(org: string, id: string): Promise<IConsultation> {
    return this._requestObs('get', EMR_API.consultations.base + '/' + id, {
      query: { organization: org }
    });
  }

  getConsultations(filters: any): Promise<IConsultation[]> {
    return this._requestObs('get', EMR_API.consultations.base, {
      query: filters
    });
  }

  getLatestConsultation(
    customerId: String,
    type?: string
  ): Promise<IConsultation[]> {
    return this._requestObs(
      'get',
      EMR_API.consultations.base +
      EMR_API.consultations.latest +
      '?customerId=' +
      customerId +
      (type ? '&type=' + type : '')
    );
  }

  saveConsultation(consultation: NewConsultationDTO): Promise<IConsultation> {
    return this._requestObs('post', EMR_API.consultations.base, {
      body: consultation
    });
  }

  updateConsultation(
    id: string,
    consultation: UpdateConsultationDTO
  ): Promise<IConsultation> {
    return this._requestObs('put', EMR_API.consultations.base + '/' + id, {
      body: consultation
    });
  }

  //////////////////
  /// NOTES
  //////////////////
  getNotes(filters?): Promise<Note> {
    return this._requestObs('get', EMR_API.notes.base, { query: filters });
  }

  saveNote(note: Partial<Note>): Promise<Note> {
    return this._requestObs('post', EMR_API.notes.base, { body: note });
  }

  updateNote(note: Partial<Note>): Promise<Note> {
    return this._requestObs(
      'put',
      EMR_API.notes.base + EMR_API.notes.single.replace(':id', note._id),
      { body: note }
    );
  }

  //////////////////
  /// FILES
  //////////////////
  getFiles(customerId: string): Promise<IFile[]> {
    return this._requestObs(
      'get',
      EMR_API.customers.base +
      EMR_API.customers.files.replace(':id', customerId)
    );
  }

  getFile(customerId: string, fileName: string): Promise<IFile> {
    return this._requestObs(
      'get',
      EMR_API.customers.base + '/' + customerId + '/files/' + fileName
    );
  }

  getLabResultFile(resourceId: string, fileName: string): Promise<IFile> {
    return this._requestObs(
      'get',
      EMR_API.labResult.base +
      EMR_API.labResult.getFile
        .replace(':resourceId', resourceId)
        .replace(':fileName', fileName)
    );
  }

  uploadFile(customerId: string, file: IFile): Promise<UploadFileResponse> {
    return this._requestObs(
      'put',
      `${EMR_API.customers.base}/${customerId}/files/${file.name}`,
      {
        body: { ...file }
      }
    );
  }

  uploadLabResult(
    file: IFile,
    folder: string,
    faxId: string,
    customerId?: string
  ): Promise<any> {
    return this._requestObs(
      'put',
      EMR_API.labResult.base +
      EMR_API.labResult.uploadFile
        .replace(':resourceId', customerId ? customerId : faxId)
        .replace(':faxId', faxId),
      {
        body: { ...file, folder }
      }
    );
  }

  deleteFile(
    customerId: string,
    resourceName: string,
    folder?: string
  ): Promise<any> {
    const endpoint = folder
      ? `${EMR_API.customers.base}/${customerId}/files/${resourceName}?folder=${folder}`
      : `${EMR_API.customers.base}/${customerId}/files/${resourceName}`;

    return this._requestObs('delete', endpoint);
  }

  //////////////////////
  ///  PRESCRIPTIONS
  ////////////////////////
  getPrescription(id: string): Promise<Prescription> {
    return this._requestObs('get', EMR_API.prescriptions.base + '/' + id);
  }

  getAllPrescriptions(filters?: PrescriptionsQuery): Promise<Prescription[]> {
    return this._requestObs('get', EMR_API.prescriptions.base, {
      query: filters
    });
  }

  createPrescription(prescription: PrescriptionDTO): Promise<Prescription> {
    return this._requestObs('post', EMR_API.prescriptions.base, {
      body: prescription
    });
  }

  updatePrescription(
    id: string,
    prescription: UpdatePrescriptionDTO
  ): Promise<Prescription> {
    return this._requestObs('put', EMR_API.prescriptions.base + '/' + id, {
      body: prescription
    });
  }
  prescrptions = {
    get: async (id: string): Promise<Prescription> =>
      await this._requestObs('get', EMR_API.prescriptions.base + '/' + id),
    list: async (filters?: any): Promise<Prescription[]> =>
      await this._requestObs('get', EMR_API.prescriptions.base, {
        query: filters
      }),
    update: async (
      id: string,
      prescription: UpdatePrescriptionDTO
    ): Promise<Prescription> =>
      await this._requestObs('put', EMR_API.prescriptions.base + '/' + id, {
        body: prescription
      }),
    create: async (prescription: PrescriptionDTO): Promise<Prescription> =>
      await this._requestObs('post', EMR_API.prescriptions.base, {
        body: prescription
      }),
    delete: async (id: string): Promise<void> =>
      await this._requestObs('delete', EMR_API.prescriptions.base + '/' + id)
  };

  //////////////////////
  ///  DRUGS
  ////////////////////////
  getDrugs(
    filters?: FilterQuery<Drug> & {
      din?: string;
      dins?: string[];
      productId?: string;
      mitteType?: string;
      name?: string;
    }
  ): Promise<Drug[]> {
    return this._requestObs('get', EMR_API.drugs.base, {
      query: filters
    });
  }

  saveDrug(drug: DrugDTO): Promise<Drug> {
    return this._requestObs('post', EMR_API.drugs.base, {
      body: drug
    });
  }

  updateDrug(id: string, drug: DrugDTO): Promise<Drug> {
    return this._requestObs('put', EMR_API.drugs.base + '/' + id, {
      body: drug
    });
  }

  /////////////////
  /// PRODUCTS
  /////////////////

  getCategories(): Promise<ICategoryItem[]> {
    return this._requestObs(
      'get',
      EMR_API.products.base + EMR_API.products.categories
    );
  }

  getProductsInCategory(category: string): Promise<IProductItem[]> {
    return this._requestObs(
      'get',
      EMR_API.products.base + (category ? '/' + category : '')
    );
  }

  //Get V2 product by Id
  getProductById(productId: string): Promise<IProductV2> {
    return this._requestObs(
      'get',
      EMR_API.productsv2.base +
      EMR_API.productsv2.single.replace(':id', productId)
    );
  }

  //////////////////
  ///  MESSAGES
  /////////////////

  getMessages(filters: any): Promise<Message[]> {
    return this._requestObs(
      'get',
      EMR_API.messages.base + EMR_API.messages.list,
      { query: filters }
    );
  }

  sendMessage(message: MessageDTO): Promise<Message> {
    return this._requestObs(
      'post',
      EMR_API.messages.base + EMR_API.messages.outbound,
      { body: message }
    );
  }

  sendRx(rx: Prescription) {
    return this._requestObs(
      'post',
      EMR_API.messages.base + EMR_API.messages.faxRx,
      { body: { ...rx } }
    );
  }

  uploadInboxFile(
    customerId: string,
    filename: string,
    base64: string
  ): Promise<any> {
    return this._requestObs(
      'put',
      EMR_API.messages.base + EMR_API.messages.upload,
      { body: { customerId, filename, data: base64 } }
    );
  }

  sendInvoiceToPatient(invoiceId, toEmail) {
    return this._requestObs(
      'post',
      EMR_API.invoices.base + EMR_API.invoices.sendToPatient,
      { body: { toEmail, invoiceId } }
    );
  }
  ///////////////////////
  ///  Appointments
  //////////////////////
  getAppointmentById(id: string) {
    return this._requestObs('get', EMR_API.appointments.base + `/${id}`);
  }

  getAppointments(query: GetAppointmentsQueryDto): Promise<IAppointment[]> {
    const params = convertToHttpParams(query);
    return this._requestObs('get', EMR_API.appointments.base, {
      query: params
    });
  }

  getMySeenAppointments(
    seenAtDay: string,
    brand: string
  ): Promise<IAppointment[]> {
    return this._requestObs(
      'get',
      EMR_API.appointments.base + EMR_API.appointments.mySeen,
      {
        query: {
          seenAtDay: seenAtDay,
          brand
        }
      }
    );
  }

  createAppointment(appointment: CreateAppointmentDto): Promise<IAppointment> {
    return this._requestObs('post', EMR_API.appointments.base, {
      body: appointment
    });
  }

  updateAppointment(
    id: string,
    appointment: UpdateAppointmentDTO
  ): Promise<IAppointment> {
    return this._requestObs('put', EMR_API.appointments.base + '/' + id, {
      body: appointment
    });
  }

  pullAppointment(): Promise<IAppointment> {
    return this._requestObs(
      'put',
      EMR_API.appointments.base + EMR_API.appointments.pull,
      null
    );
  }

  //////////////////
  ///  SUBSCRIPTIONS
  /////////////////

  getSubscriptions(query: any) {
    return this._requestObs('get', EMR_API.subscriptions.base, {
      query: query
    });
  }

  cancelSubscription(id: string, params: ICancelSubscriptionQuery = {}) {
    return this._requestObs(
      'delete',

      EMR_API.subscriptions.base +
      EMR_API.subscriptions.single.replace(':id', id),
      {
        body: params
      }
    );
  }

  //////////////////
  ///  INVOICES
  /////////////////
  getInvoices(query: any) {
    return this._requestObs('get', EMR_API.invoices.base, {
      query
    });
  }

  //////////////////////
  ///  Lab Req
  ////////////////////////
  getLabReqs(filters?: any): Promise<ILabReq[]> {
    return this._requestObs('get', EMR_API.labReq.base, {
      query: filters
    });
  }

  saveLabReq(labReq: ILabReqDTO): Promise<ILabReq> {
    return this._requestObs('post', EMR_API.labReq.base, {
      body: labReq
    });
  }

  updateLabReq(id: string, labReq: ILabReqDTO): Promise<ILabReq> {
    return this._requestObs('put', EMR_API.labReq.base + '/' + id, {
      body: labReq
    });
  }

  //////////////////
  /// LAB RESULTS
  //////////////////
  saveLabResult(labResult: any): Promise<LabResult> {
    return this._requestObs('post', EMR_API.labResult.base, {
      body: labResult
    });
  }

  /**
   * Create a lab result from manual form data. If file is provided, backend uploads PDF and creates lab result in one request.
   */
  createLabResult(
    customerId: string,
    labResult: ILabResultDTO,
    file?: { buffer: string; name: string; mimetype: string }
  ): Promise<LabResult> {
    return this._requestObs(
      'post',
      EMR_API.labResult.base + EMR_API.labResult.createFromForm,
      { body: { customerId, labResult, ...(file && { file }) } }
    );
  }

  submitLabResultPdf(
    customerId: string,
    file: { buffer: string; name: string; mimetype: string }
  ): Promise<{ faxId: string; fileName: string }> {
    return this._requestObs(
      'post',
      EMR_API.labResult.base + EMR_API.labResult.submitPdf,
      { body: { customerId, file } }
    );
  }

  updateLabResult(id: string, labResult: ILabResultDTO): Promise<LabResult> {
    return this._requestObs('put', EMR_API.labResult.base + '/' + id, {
      body: labResult
    });
  }

  getLabResultPdfSignedUrl(
    id: string
  ): Promise<{ success: boolean; signedUrl?: string }> {
    return this._requestObs(
      'get',
      EMR_API.labResult.base + EMR_API.labResult.pdf.replace(':id', id)
    );
  }

  getLabResult(id: string): Promise<LabResult> {
    return this._requestObs('get', EMR_API.labResult.base + '/' + id);
  }

  getLabResults(filters?: any): Promise<LabResult[]> {
    return this._requestObs('get', EMR_API.labResult.base, {
      query: filters
    });
  }

  getLabResultsByCustomer(
    filters: any,
    customerId: string
  ): Promise<LabResult[]> {
    return this._requestObs('get', EMR_API.labResult.base, {
      query: { ...filters, customer: customerId }
    });
  }

  mergeLabResult(
    id: string,
    body: {
      patientData: LabResultPatientData;
      faxId: string;
      runId: string;
      docUrl: string;
    }
  ): Promise<LabResult> {
    return this._requestObs(
      'post',
      EMR_API.labResult.base + EMR_API.labResult.merge.replace(':id', id),
      {
        body
      }
    );
  }

  //////////////////
  /// ENCOUNTERS
  //////////////////

  startChat(customerId: string): Promise<JoinChatResponse> {
    return this._requestObs('put', EMR_API.chat.base + EMR_API.chat.join, {
      body: {
        customerId
      }
    });
  }

  refreshToken(token: string): Promise<IChatAuth> {
    return this._requestObs(
      'put',
      EMR_API.chat.base + EMR_API.chat.refreshToken,
      { body: { token } }
    );
  }

  forwardEncounter(customerId: string, to: 'CS' | 'PH') {
    return this._requestObs(
      'post',
      EMR_API.messages.base + EMR_API.messages.forward,
      { body: { to, customerId } }
    );
  }

  joinVideo(customerId: string): Promise<IAppointmentVirtualMeeting> {
    return this._requestObs(
      'put',
      EMR_API.appointments.base + EMR_API.appointments.meeting,
      {
        body: {
          customerId
        }
      }
    );
  }

  ////////////////////////
  // Admin Users (from CRM) FUNCTIONS
  ////////////////////////
  getAdminUserById(id: string): Promise<IAdminUser> {
    return this._requestObs('get', EMR_API.users.base + '/' + id);
  }

  getAdminUsers(filters: CRMUserFilters) {
    return this._requestObs('get', EMR_API.users.base, {
      query: { ...filters }
    });
  }

  updateAdminUser(id: string, user: Partial<ICRMUser>) {
    return this._requestObs('put', EMR_API.users.base + '/' + id, {
      body: user
    });
  }

  getInvoiceEmailTemplate(invoiceId: string) {
    return this._requestObs(
      'get',
      EMR_API.emailTemplates.base + EMR_API.emailTemplates.invoice,
      { query: { invoiceId } }
    );
  }

  activities = {
    list: async (customerId: string): Promise<IBaseListResponse<IActivity>> =>
      await this._requestObs('get', EMR_API.activities.base, {
        query: { customerId }
      }),
    create: async (data: CreateActivityDTO): Promise<IActivity> =>
      await this._requestObs('post', EMR_API.activities.base, {
        body: data
      })
  };

  pharmacies = {
    /** Active only; pass `{ includeDisabled: true }` to include disabled pharmacies. */
    list: async (query: IPharmacyListQuery = {}): Promise<IPharmacy[]> =>
      await this._requestObs('get', EMR_API.pharmacies.base, {
        query: query.includeDisabled ? { includeDisabled: 'true' } : undefined
      }),
    get: async (id: string): Promise<IPharmacy> =>
      await this._requestObs(
        'get',
        EMR_API.pharmacies.base + EMR_API.pharmacies.single.replace(':id', id)
      ),
    create: async (payload: ICreatePharmacyRequest): Promise<IPharmacy> =>
      await this._requestObs('post', EMR_API.pharmacies.base, {
        body: payload
      }),
    update: async (
      id: string,
      payload: IUpdatePharmacyRequest
    ): Promise<IPharmacy> =>
      await this._requestObs(
        'put',
        EMR_API.pharmacies.base + EMR_API.pharmacies.single.replace(':id', id),
        {
          body: payload
        }
      ),
    delete: async (id: string): Promise<{ success: boolean }> =>
      await this._requestObs(
        'delete',
        EMR_API.pharmacies.base + EMR_API.pharmacies.single.replace(':id', id)
      )
  };
}

export namespace CarePortals {
  EMR;
}
